/**
 * useAnalyticsSummary - Memoized summary metrics and distributions
 *
 * Extracted from useAnalyticsData.js. Pure derivation layer over the report
 * cache: filters reports to the active time window and computes the summary
 * stat tiles and HRO/KPI pie-chart distributions.
 *
 * Performance metrics (responseTime, open/closed) respect per-org
 * analyticsCutoffDates ("clean slate"); all other metrics use full history.
 *
 * @param {Array} allReports - Full report cache (from useAnalyticsReports)
 * @param {number} activeFilter - Active time filter index
 * @param {Object} analyticsCutoffDates - Per-org cutoff timestamps
 */

import { useMemo } from 'react';

import {
  isQueryValid,
  getStartAndEndTimes,
  filterReports,
} from '../util/analytics/queryUtils';
import {
  HRO_COLORS,
  getHroLabels,
  CATEGORICAL_PALETTE,
} from '../util/analytics/colorUtils';
import { TIME_FILTER_CONFIGS } from '../util/analytics/constants';

export default function useAnalyticsSummary(allReports, activeFilter, analyticsCutoffDates) {
  // Memoize active filter config to avoid recalculation
  const activeFilterConfig = useMemo(() => {
    return TIME_FILTER_CONFIGS[activeFilter] || null;
  }, [activeFilter]);

  // Memoize filtered reports based on active filter
  const filteredReportsData = useMemo(() => {
    if (!activeFilterConfig || allReports.length === 0) {
      return { data: [], startTime: 0, endTime: 0 };
    }
    const [queryMode, timeAmount, timeStride] = activeFilterConfig;
    if (!isQueryValid(queryMode, timeAmount, timeStride)) {
      return { data: [], startTime: 0, endTime: 0 };
    }
    const [startTime, endTime] = getStartAndEndTimes(queryMode, timeAmount * timeStride);
    const data = filterReports(allReports, startTime, endTime);
    return { data, startTime, endTime, queryMode, timeAmount, timeStride };
  }, [activeFilterConfig, allReports]);

  // Memoize summary metrics computation (heavy calculation over all filtered reports)
  // Performance metrics (responseTime, openReports) respect per-org analyticsCutoffDates for "clean slate"
  // All other metrics (submittedReports, urgentReports, hroCounts, kpiCounts) show full history
  const summaryMetrics = useMemo(() => {
    const { data } = filteredReportsData;
    if (data.length === 0) {
      return {
        submittedReports: 0,
        responseTime: '---',
        openReports: 0,
        closedReports: 0,
        urgentReports: 0,
        hroCounts: { PF: 0, RS: 0, SO: 0, CR: 0, DE: 0 },
        kpiCounts: {},
      };
    }

    // Filter reports by per-org cutoff date for performance metrics only
    const hasCutoffs = Object.keys(analyticsCutoffDates).length > 0;
    const performanceReports = hasCutoffs
      ? data.filter((report) => {
          const orgCutoff = analyticsCutoffDates[report.organization];
          return !orgCutoff || report.timestamp >= orgCutoff;
        })
      : data;

    // Performance metrics - calculated from reports after cutoff
    let timeElapsed = 0;
    let totalReportsWithTime = 0;
    let open = 0;
    let closed = 0;

    performanceReports.forEach((report) => {
      if (report.hasOwnProperty('timeElapsedUntilResponse')) {
        timeElapsed += report.timeElapsedUntilResponse;
        totalReportsWithTime += 1;
      }

      if (report.status === 'Closed') {
        closed++;
      } else {
        open++;
      }
    });

    // Full history metrics - calculated from all reports (ignore cutoff)
    let urgent = 0;
    const hroCounts = { PF: 0, RS: 0, SO: 0, CR: 0, DE: 0 };
    const kpiCounts = {};

    data.forEach((report) => {
      if (report.isUrgent || report.urgent) {
        urgent++;
      }

      const hro = report.HROs?.[0] || report.HRO_AI;
      if (hro && hroCounts.hasOwnProperty(hro)) {
        hroCounts[hro]++;
      }

      const kpi = report.KPIs?.[0] || (!report.AIOverride && report.KPI_AI);
      if (kpi) {
        kpiCounts[kpi] = (kpiCounts[kpi] || 0) + 1;
      }
    });

    return {
      submittedReports: data.length,
      responseTime: totalReportsWithTime
        ? Number((timeElapsed / (1000 * 60 * 60) / totalReportsWithTime).toFixed(2))
        : '---',
      openReports: open,
      closedReports: closed,
      urgentReports: urgent,
      hroCounts,
      kpiCounts,
    };
  }, [filteredReportsData, analyticsCutoffDates]);

  // Memoize HRO distribution for pie chart
  const hroDistribution = useMemo(() => {
    const hroLabels = getHroLabels();
    return Object.entries(summaryMetrics.hroCounts)
      .filter(([_, v]) => v > 0)
      .map(([k, v]) => ({ label: hroLabels[k] || k, value: v, color: HRO_COLORS[k] || '#999' }));
  }, [summaryMetrics.hroCounts]);

  // Memoize KPI distribution - all items sorted by value (component handles display limit)
  const kpiDistribution = useMemo(() => {
    const sortedKpis = Object.entries(summaryMetrics.kpiCounts)
      .sort((a, b) => b[1] - a[1]);
    return sortedKpis.map(([k, v], i) => ({
      label: k,
      value: v,
      color: CATEGORICAL_PALETTE[i % CATEGORICAL_PALETTE.length],
    }));
  }, [summaryMetrics.kpiCounts]);

  return {
    filteredReportsData,
    summaryMetrics,
    hroDistribution,
    kpiDistribution,
  };
}
